/**
 * Client-minted identifiers.
 *
 * The contract asks for **UUIDv7** for `order_id` (and recommends it for
 * `Idempotency-Key`) because those ids are stored, sorted and replayed
 * server-side; a v4 would work but would scatter across the index. It is fifteen
 * lines, so it is written rather than depended on — this repo's dependency list
 * is a perf-budget decision, not an accident.
 */

const HEX = '0123456789abcdef';

function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  globalThis.crypto.getRandomValues(out);
  return out;
}

function hex(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) {
    s += HEX[(b >> 4) & 0xf]! + HEX[b & 0xf]!;
  }
  return s;
}

/**
 * A UUIDv7: 48 bits of Unix milliseconds, then version/variant, then randomness.
 * Time-ordered, so two ids minted a second apart sort in that order.
 */
export function uuidv7(now: number = Date.now()): string {
  const bytes = randomBytes(16);
  const ms = BigInt(now);

  bytes[0] = Number((ms >> 40n) & 0xffn);
  bytes[1] = Number((ms >> 32n) & 0xffn);
  bytes[2] = Number((ms >> 24n) & 0xffn);
  bytes[3] = Number((ms >> 16n) & 0xffn);
  bytes[4] = Number((ms >> 8n) & 0xffn);
  bytes[5] = Number(ms & 0xffn);

  bytes[6] = (bytes[6]! & 0x0f) | 0x70; // version 7
  bytes[8] = (bytes[8]! & 0x3f) | 0x80; // RFC 4122 variant

  const h = hex(bytes);
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * A fresh `Idempotency-Key`.
 *
 * One key per *intent*, not per attempt: a retry of the same submission must
 * carry the same key or the server has no way to recognise it as a retry. Mint
 * this when the guest decides, store it beside the thing it protects, and reuse
 * it for every attempt.
 */
export function newIdempotencyKey(): string {
  return uuidv7();
}
